// CP-212: one source of truth for the link text and accessible names on every sailing card
// (Home shelves, Hot Deals, Best Deals, Browse). Replaces the generic "View Details",
// "View Deal" and "View Itinerary & Prices" labels.
//
// Visible text:   "See 7-night Caribbean sailing"      (nights + the API destination field when it is a specific canonical region)
// Accessible name: visible text first, then ship and sail date, so two cards that share nights
//                  and destination ("2-Night Mediterranean" x6) still have unique names.
//                  The visible text always leads the name (WCAG 2.5.3 Label in Name).
// Nothing is invented: a missing nights, destination, ship or date is left out, never "undefined".

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function clean(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

// Whole nights as a positive integer, or 0 when the value is not a usable number.
export function sailingNights(duration) {
  const n = Math.round(Number(duration));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// "Jun 12, 2027" from an ISO-ish string. Read from the YYYY-MM-DD text itself so the
// shopper's timezone can never shift the day. '' when there is no real date.
export function sailingDateLabel(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(clean(String(value == null ? '' : value)));
  if (!m) return '';
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  return `${MONTHS[month - 1]} ${day}, ${m[1]}`;
}

function sailingDate(row) {
  return row ? sailingDateLabel(row.sail_date || row.departure_date) : '';
}

// "7-night Caribbean sailing" / "7-night sailing" / "Caribbean sailing" / "sailing".
// The API `destination` is the line's own region column mapped to a coarse canonical label
// (backend destination_taxonomy.CANONICAL_LABELS); a value the taxonomy cannot map comes through as
// the raw source string ("The Four Queens Celebration", "Bahamas, Weekend", "Miami To Southampton"),
// and the catch-alls ("World & Exotic" holds Galapagos, repositioning and world cruises, so it can sit
// under a "Southern Caribbean Cruise" title) say nothing reliable about the sailing. Only a specific
// canonical label is shown in the link; anything else falls back to "See N-night sailing", and the
// card title above stays the authority on what the cruise is.
export const SPECIFIC_DESTINATION_LABELS = [
  'Caribbean', 'Bahamas', 'Bermuda', 'Mexican Riviera', 'Panama Canal', 'Alaska', 'Hawaii',
  'Pacific Coast', 'Pacific Northwest', 'Canada & New England', 'Europe', 'Mediterranean',
  'Northern Europe', 'Transatlantic', 'Asia', 'Australia & New Zealand', 'South Pacific', 'South America',
];

export function sailingDestination(row) {
  if (!row || row.destination_unmapped === true) return '';
  const label = clean(row.destination);
  return SPECIFIC_DESTINATION_LABELS.includes(label) ? label : '';
}

export function sailingCore(row) {
  const nights = sailingNights(row && row.duration);
  const destination = sailingDestination(row);
  return [nights ? `${nights}-night` : '', destination, 'sailing'].filter(Boolean).join(' ');
}

// Visible link text.
export function sailingLinkText(row) {
  const core = sailingCore(row);
  return core === 'sailing' ? 'See sailing details' : `See ${core}`;
}

// "7-night Caribbean sailing, Eurodam, Jan 2, 2027": which sailing, without the verb.
export function sailingSummary(row) {
  const ship = clean(row && row.ship_name);
  return [sailingCore(row), ship, sailingDate(row)].filter(Boolean).join(', ');
}

// Accessible name for the sailing link: visible text first, then ship and date.
export function sailingLinkAriaLabel(row) {
  const ship = clean(row && row.ship_name);
  return [sailingLinkText(row), ship, sailingDate(row)].filter(Boolean).join(', ');
}

// Accessible name for a secondary action on a card (price history, CruiseDirect link):
// the visible text first, then the sailing it belongs to, then an optional suffix.
//   actionAriaLabel('Check price on CruiseDirect', row, '(opens in a new tab)')
export function actionAriaLabel(visibleText, row, suffix = '') {
  const lead = clean(visibleText);
  const tail = clean(suffix);
  return `${lead}: ${sailingSummary(row)}${tail ? ` ${tail}` : ''}`;
}
